import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Heart, 
  Wind, 
  Stethoscope,
  Microscope,
  Video,
  Zap,
  CheckCircle,
  ChevronRight,
  Clock,
  Award,
  Shield,
  ArrowRight
} from 'lucide-react';

const ServicesPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      id: 1,
      icon: <Activity className="w-12 h-12" />,
      title: "Chest X-Ray",
      shortDesc: "Advanced digital radiography for accurate diagnosis",
      description: "State-of-the-art digital X-ray technology for comprehensive chest imaging and diagnosis of respiratory conditions, lung infections, and structural abnormalities with minimal radiation exposure.",
      features: [
        "Digital X-Ray Technology",
        "Same-day Results",
        "Low Radiation Exposure",
        "Expert Radiologist Reports",
        "High-Resolution Imaging",
        "Portable X-Ray Available"
      ],
      duration: "15-20 minutes",
      category: "Diagnostic Imaging",
      color: "#E86A33"
    },
    {
      id: 2,
      icon: <Wind className="w-12 h-12" />,
      title: "PFT (Pulmonary Function Test)",
      shortDesc: "Comprehensive lung function evaluation",
      description: "Complete pulmonary function testing to measure lung capacity, airflow rates, and gas exchange efficiency. Essential for diagnosing and monitoring respiratory conditions like asthma, COPD, and interstitial lung diseases.",
      features: [
        "Spirometry Testing",
        "Lung Volume Measurement",
        "Diffusion Capacity Testing",
        "Exercise Testing Available",
        "Pre & Post-Bronchodilator",
        "Detailed Analysis Reports"
      ],
      duration: "30-45 minutes",
      category: "Respiratory Testing",
      color: "#2C85B8"
    },
    {
      id: 3,
      icon: <Heart className="w-12 h-12" />,
      title: "ECG (Electrocardiogram)",
      shortDesc: "Comprehensive heart monitoring",
      description: "Non-invasive cardiac monitoring to detect arrhythmias, heart disease, and circulation issues. Quick and painless procedure providing immediate results for accurate cardiac assessment.",
      features: [
        "12-Lead ECG",
        "Immediate Results",
        "Arrhythmia Detection",
        "Heart Health Assessment",
        "Digital Recording",
        "Expert Cardiologist Review"
      ],
      duration: "10-15 minutes",
      category: "Cardiac Testing",
      color: "#E63946"
    },
    {
      id: 4,
      icon: <Activity className="w-12 h-12" />,
      title: "2D ECHO (Echocardiography)",
      shortDesc: "Advanced heart imaging technology",
      description: "Sophisticated ultrasound imaging providing real-time visualization of heart structure, function, and blood flow patterns. Essential for evaluating heart valves, chambers, and overall cardiac performance.",
      features: [
        "Real-time Heart Imaging",
        "Valve Function Assessment",
        "Chamber Size Analysis",
        "Blood Flow Evaluation",
        "Doppler Studies",
        "Color Flow Mapping"
      ],
      duration: "30-45 minutes",
      category: "Cardiac Imaging",
      color: "#1B4965"
    },
    {
      id: 5,
      icon: <Microscope className="w-12 h-12" />,
      title: "Allergy Testing",
      shortDesc: "Comprehensive allergen identification",
      description: "Advanced allergy testing through skin prick tests and blood analysis to identify specific allergens causing your symptoms. Helps create personalized treatment and prevention plans.",
      features: [
        "Skin Prick Testing",
        "IgE Blood Tests",
        "Food Allergy Panel",
        "Environmental Allergen Testing",
        "Drug Allergy Testing",
        "Detailed Allergy Report"
      ],
      duration: "45-60 minutes",
      category: "Immunology",
      color: "#FF9B6E"
    },
    {
      id: 6,
      icon: <Video className="w-12 h-12" />,
      title: "Bronchoscopy",
      shortDesc: "Advanced airway visualization",
      description: "Minimally invasive procedure using a flexible bronchoscope to examine airways, collect samples, and perform therapeutic interventions. Performed under sedation for patient comfort.",
      features: [
        "Flexible Bronchoscopy",
        "Diagnostic & Therapeutic",
        "Tissue Biopsy",
        "Bronchoalveolar Lavage",
        "Foreign Body Removal",
        "Sedation Available"
      ],
      duration: "30-60 minutes",
      category: "Interventional",
      color: "#2C5F7C"
    },
    {
      id: 7,
      icon: <Zap className="w-12 h-12" />,
      title: "Thoracoscopy",
      shortDesc: "Minimally invasive chest surgery",
      description: "Advanced minimally invasive surgical procedure for diagnosis and treatment of chest conditions. Uses small incisions and a camera for better visualization and faster recovery.",
      features: [
        "Video-Assisted Surgery",
        "Minimal Scarring",
        "Faster Recovery",
        "Pleural Biopsy",
        "Fluid Drainage",
        "Expert Surgical Team"
      ],
      duration: "1-2 hours",
      category: "Surgical",
      color: "#62B6CB"
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
          color: #1a1a1a;
        }

        .services-page {
          width: 100%;
          min-height: 100vh;
          background: linear-gradient(135deg, #F8FAFB 0%, white 100%);
        }

        /* Hero Section */
        .services-hero {
          position: relative;
          height: 60vh;
          min-height: 500px;
          background: linear-gradient(135deg, #1B4965 0%, #2C5F7C 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .services-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 70%;
          height: 200%;
          background: radial-gradient(circle, rgba(232, 106, 51, 0.15) 0%, transparent 70%);
          animation: rotate 30s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .services-hero-content {
          text-align: center;
          color: white;
          z-index: 2;
          max-width: 900px;
          padding: 2rem;
        }

        .services-label {
          font-size: 0.95rem;
          font-weight: 700;
          color: #FF9B6E;
          letter-spacing: 4px;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
          animation: fadeInDown 0.8s ease;
        }

        .services-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: 4.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.1;
          animation: fadeInUp 0.8s ease 0.2s both;
        }

        .services-hero-subtitle {
          font-size: 1.3rem;
          opacity: 0.95;
          font-weight: 300;
          line-height: 1.6;
          animation: fadeInUp 0.8s ease 0.4s both;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Services Grid */
        .services-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 6rem 2rem;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 3rem;
        }

        .service-card {
          background: white;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          cursor: pointer;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 6px;
          background: linear-gradient(90deg, #E86A33, #FF9B6E);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .service-card:hover::before {
          transform: scaleX(1);
        }

        .service-card:hover {
          transform: translateY(-15px);
          box-shadow: 0 20px 60px rgba(27, 73, 101, 0.15);
        }

        .service-card-header {
          padding: 3rem;
          display: flex;
          align-items: flex-start;
          gap: 2rem;
          border-bottom: 1px solid #f0f0f0;
        }

        .service-icon-wrapper {
          width: 90px;
          height: 90px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          transition: all 0.4s ease;
        }

        .service-card:hover .service-icon-wrapper {
          transform: rotate(5deg) scale(1.05);
        }

        .service-header-content {
          flex: 1;
        }

        .service-category {
          font-size: 0.75rem;
          font-weight: 700;
          color: #E86A33;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 0.8rem;
        }

        .service-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1B4965;
          margin-bottom: 0.8rem;
          font-family: 'Playfair Display', serif;
        }

        .service-short-desc {
          font-size: 1rem;
          color: #666;
          line-height: 1.6;
        }

        .service-card-body {
          padding: 2.5rem 3rem;
        }

        .service-description {
          font-size: 1.05rem;
          color: #666;
          line-height: 1.9;
          margin-bottom: 2rem;
        }

        .service-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          font-size: 0.95rem;
          color: #1a1a1a;
        }

        .feature-icon {
          color: #E86A33;
          flex-shrink: 0;
        }

        .service-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 2rem;
          border-top: 1px solid #f0f0f0;
        }

        .service-duration {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          color: #666;
          font-weight: 600;
        }

        .service-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #E86A33;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .service-link:hover {
          gap: 1rem;
          color: #1B4965;
        }

        /* Stats Section */
        .services-stats {
          background: linear-gradient(135deg, #1B4965 0%, #2C5F7C 100%);
          padding: 5rem 2rem;
          margin: 4rem 0;
        }

        .stats-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 3rem;
        }

        .stat-item {
          text-align: center;
          color: white;
        }

        .stat-number {
          font-size: 4rem;
          font-weight: 800;
          color: #FF9B6E;
          font-family: 'Playfair Display', serif;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 1.1rem;
          opacity: 0.9;
          font-weight: 500;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .services-hero-title {
            font-size: 2.5rem;
          }

          .services-hero-subtitle {
            font-size: 1.1rem;
          }

          .services-grid {
            grid-template-columns: 1fr;
          }

          .service-card-header {
            flex-direction: column;
            padding: 2rem;
          }

          .service-card-body {
            padding: 2rem;
          }

          .service-features {
            grid-template-columns: 1fr;
          }

          .service-footer {
            flex-direction: column;
            gap: 1.5rem;
            align-items: flex-start;
          }

          .stat-number {
            font-size: 3rem;
          }
        }
      `}</style>

      <div className="services-page">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="services-hero-content">
            <div className="services-label">Our Medical Services</div>
            <h1 className="services-hero-title">Comprehensive Healthcare Solutions</h1>
            <p className="services-hero-subtitle">
              State-of-the-art diagnostic and therapeutic services delivered by expert medical professionals using advanced technology
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="services-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                className="service-card"
                onMouseEnter={() => setActiveService(service.id)}
                onMouseLeave={() => setActiveService(null)}
                style={{
                  animation: `fadeInUp 0.6s ease ${index * 0.1}s both`
                }}
              >
                <div className="service-card-header">
                  <div 
                    className="service-icon-wrapper"
                    style={{background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)`}}
                  >
                    {service.icon}
                  </div>
                  <div className="service-header-content">
                    <div className="service-category">{service.category}</div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-short-desc">{service.shortDesc}</p>
                  </div>
                </div>

                <div className="service-card-body">
                  <p className="service-description">{service.description}</p>

                  <div className="service-features">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <CheckCircle size={18} className="feature-icon" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="service-footer">
                    <div className="service-duration">
                      <Clock size={18} />
                      <span>{service.duration}</span>
                    </div>
                    <a href={`/services/${service.title.toLowerCase().replace(/\s+/g, '-')}`} className="service-link">
                      Learn More
                      <ArrowRight size={18} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="services-stats">
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">7+</div>
              <div className="stat-label">Specialized Services</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">10K+</div>
              <div className="stat-label">Patients Served</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Emergency Support</div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;